<template>
  <v-chart :option="chartOption" autoresize/>
</template>

<script>
import {use} from 'echarts/core';
import {CanvasRenderer} from 'echarts/renderers';
import {BarChart} from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent
} from 'echarts/components';
import VChart from 'vue-echarts';
import {defineComponent, computed} from 'vue';
import i18n from "../../lang/i18n";

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent
]);

export default defineComponent({
  components: {
    VChart,
  },
  props: ["chartData", "locale"],
  provide: {},
  setup(props) {
    const chartOption = computed(() => {
      let locale = props.locale
      let chartData = props.chartData

      let data = chartData.data
      let colors = chartData.colors
      let legends = locale === "en" ? chartData.legendEn : chartData.legendVi
      let xAxis = locale === "en" ? chartData.en : chartData.vi

      // Fix data length to match xAxis labels length
      const adjustedData = data.map(series => {
        const adjustedSeries = [...series]
        // Extend with zeros if data is shorter than labels
        while (adjustedSeries.length < xAxis.length) {
          adjustedSeries.push(0)
        }
        // Trim if data is longer than labels
        return adjustedSeries.slice(0, xAxis.length)
      })
      // Sort xAxis (Loại SOS) alphabetically by display name and reorder data columns accordingly
      const localeTag = locale === 'en' ? 'en' : 'vi'
      const indices = xAxis.map((_, i) => i).sort((a, b) => xAxis[a].localeCompare(xAxis[b], localeTag, { sensitivity: 'base' }))
      const sortedXAxis = indices.map(i => xAxis[i])
      const sortedData = adjustedData.map(series => indices.map(i => series[i]))

      data = sortedData
      xAxis = sortedXAxis

      return {
        color: colors,
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'cross'
          },
          padding: 0,
          formatter: function (params) {
            // let sc = i18n.t('ui.secondWrapped')
            return `
            <p style="font-size: 16px; font-weight: bold; padding: 8px; margin: 0px;">${params.name}</p>
            <hr style="margin: 0px;"></hr>
            <p  style="padding: 8px 8px 0px; margin: 0px;">${params.marker} ${params.seriesName}</p>
            <p style="font-size: 24px; padding: 20px; margin: 0px;"><b>${params.value}s</b></p>`
          }
        },
        legend: {
          left: 'right',
          icon: 'circle',
          // sort legend entries alphabetically left-to-right by display name
          data: [...legends].sort((a, b) => a.localeCompare(b, locale === 'en' ? 'en' : 'vi', { sensitivity: 'base' }))
        },
        grid: {
          left: '3%',
          right: '1%',
          bottom: xAxis.length > 6 ? '25%' : '15%',
        },
        dataZoom: xAxis.length > 6 ? [
          {
            type: 'inside',
            xAxisIndex: [0],
            start: 0,
            end: Math.min(100, Math.round(600 / xAxis.length)),
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            moveOnMouseWheel: true
          },
          {
            type: 'slider',
            xAxisIndex: [0],
            start: 0,
            end: Math.min(100, Math.round(600 / xAxis.length)),
            bottom: '5%',
            height: '20px',
            showDetail: false,
            handleStyle: {
              color: '#1890FF',
              borderColor: '#1890FF'
            }
          }
        ] : [],

        yAxis: {
          show: true,
          type: 'value',
          position: 'left',
          axisLine: {
            show: true,
            onZero: true,
            symbol: 'none',
            symbolSize: [10, 15],
            symbolOffset: [0, 0],
          },
          axisTick: {
            show: true
          },
          boundaryGap: [0, 0]
        },
        xAxis: {
          axisLine: {onZero: false},
          axisTick: {show: true},
          type: 'category',
          data: xAxis,
          axisLabel: {
            rotate: 45,
            interval: 0,
            fontSize: 10,
            formatter: function (value) {
              // Truncate long labels and add ellipsis
              if (value.length > 15) {
                return value.substring(0, 15) + '...'
              }
              return value
            }
          }
        },
        series: [
          {
            itemStyle: {
              borderRadius: [5, 5, 0, 0]
            },
            barGap: 0.01,
            name: legends[0],
            type: 'bar',
            data: data[0]
          },
          {
            itemStyle: {
              borderRadius: [5, 5, 0, 0]
            },
            barCategoryGap: '50%',
            name: legends[1],
            type: 'bar',
            data: data[1]
          }
        ]
      }
    })

    return {chartOption};
  },
});


</script>

<style scoped>
</style>